"use client";

import React, { useRef, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { SubPromotion } from '@/services/dataTypes';
import { getBaseImageUrl, getPromotionHref } from '@/constants/hooks';

interface Props {
    subPromotions: SubPromotion[];
    domain: string;
    promotionSlug: string;
}

const SubPromotionSlider = ({ subPromotions, domain, promotionSlug }: Props) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const syncState = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        setCanPrev(t.scrollLeft > 4);
        setCanNext(t.scrollLeft < t.scrollWidth - t.clientWidth - 4);
    }, []);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        syncState();
        t.addEventListener('scroll', syncState, { passive: true });
        window.addEventListener('resize', syncState);
        return () => { t.removeEventListener('scroll', syncState); window.removeEventListener('resize', syncState); };
    }, [subPromotions, syncState]);

    const scroll = (dir: 'left' | 'right') => {
        trackRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    };

    if (!subPromotions?.length) return null;

    return (
        <div className="relative" style={{ isolation: 'isolate', zIndex: 10 }}>
            {/* Prev */}
            <button onClick={() => scroll('left')} disabled={!canPrev} aria-label="Prev"
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-9 h-9 flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 focus:outline-none"
                style={{
                    background: 'rgba(8,28,17,0.7)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(122,252,32,0.4)',
                    clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                    boxShadow: '0 0 14px rgba(122,252,32,0.2)',
                }}>
                <svg className="w-3.5 h-3.5" style={{ color: '#7AFC20' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Next */}
            <button onClick={() => scroll('right')} disabled={!canNext} aria-label="Next"
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-9 h-9 flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 focus:outline-none"
                style={{
                    background: 'rgba(8,28,17,0.7)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(122,252,32,0.4)',
                    clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                    boxShadow: '0 0 14px rgba(122,252,32,0.2)',
                }}>
                <svg className="w-3.5 h-3.5" style={{ color: '#7AFC20' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Edge fades */}
            <div className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none transition-opacity duration-300"
                style={{ background: 'linear-gradient(90deg, #081C11, transparent)', opacity: canPrev ? 1 : 0 }} />
            <div className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none transition-opacity duration-300"
                style={{ background: 'linear-gradient(270deg, #081C11, transparent)', opacity: canNext ? 1 : 0 }} />

            {/* Track */}
            <div ref={trackRef}
                className="flex gap-4 overflow-x-auto pb-2 pt-3"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory', overflowY: 'visible' }}>
                <style>{`.sps-track::-webkit-scrollbar{display:none}`}</style>

                {subPromotions.map((item) => (
                    <Link
                        key={item.unique_id}
                        href={getPromotionHref(item, promotionSlug)}
                        className="group relative flex-shrink-0 overflow-hidden no-underline transition-all duration-500 hover:-translate-y-1.5 hover:z-20"
                        style={{
                            width: '260px',
                            height: '200px',
                            scrollSnapAlign: 'start',
                            clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {/* Background image */}
                        {item.category_image && (
                            <Image
                                src={getBaseImageUrl(domain, item.category_image, '')}
                                alt={item.category_name}
                                fill unoptimized
                                className="object-cover transition-transform duration-700 group-hover:scale-110"
                                sizes="260px"
                            />
                        )}

                        {/* Gradient overlay */}
                        <div
                            className="absolute inset-0"
                            style={{ background: 'linear-gradient(180deg, rgba(8,28,17,0.2) 0%, rgba(8,28,17,0.85) 100%)' }}
                        />

                        {/* Hover border glow */}
                        <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                            style={{
                                boxShadow: 'inset 0 0 0 1px rgba(122,252,32,0.5)',
                                clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                            }}
                        />

                        {/* Corner accent */}
                        <div className="absolute top-0 left-0 w-6 h-[2px]" style={{ background: '#7AFC20' }} />
                        <div className="absolute top-0 left-0 w-[2px] h-6" style={{ background: '#7AFC20' }} />

                        {/* Content */}
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                            <h4
                                className="text-sm font-black leading-tight line-clamp-1 m-0 mb-1 transition-colors duration-300 group-hover:text-[#7AFC20]"
                                style={{ color: '#F5F5DC' }}
                            >
                                {item.category_name}
                            </h4>
                            {item.category_detail && (
                                <p className="text-[10px] line-clamp-1 m-0 mb-3" style={{ color: 'rgba(245,245,220,0.5)' }}>
                                    {item.category_detail}
                                </p>
                            )}
                            <div
                                className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.2em] translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400"
                                style={{ color: '#7AFC20' }}
                            >
                                Explore Offers
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default SubPromotionSlider;
